import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import SlideRenderer from '../components/presentation/SlideRenderer';
import '../components/presentation/presentation-theme.css';
import '../components/presentation/SlideRenderer.css';

// Renders one slide off-screen at the design resolution and rasterizes it —
// the same DOM/CSS the live viewer uses, just captured to an image. This
// avoids needing a server-side headless-browser render pipeline; the
// trade-off is that the exported PDF is images, not selectable text (see
// exportNotesPdf/exportQuickReferenceGuidePdf below, which stay text-based).
async function rasterizeSlide(slide, index, total, manifest) {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = '1280px';
  host.style.height = '720px';
  host.className = 'presentation-export';
  document.body.appendChild(host);
  const root = createRoot(host);
  await new Promise((resolve) => {
    root.render(React.createElement(SlideRenderer, { slide, index, total, manifest }));
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
  await Promise.all(Array.from(host.querySelectorAll('img')).map((img) => {
    if (img.complete) return img.decode?.().catch(() => {}) || Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }));
  const canvas = await html2canvas(host, { width: 1280, height: 720, scale: 2, backgroundColor: '#F7F1E7', useCORS: true });
  root.unmount();
  document.body.removeChild(host);
  return canvas.toDataURL('image/jpeg', 0.92);
}

export async function exportDeckPdf(manifest, slides, onProgress) {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [1280, 720] });
  for (let i = 0; i < slides.length; i++) {
    const dataUrl = await rasterizeSlide(slides[i], i, slides.length, manifest);
    if (i > 0) pdf.addPage([1280, 720], 'landscape');
    pdf.addImage(dataUrl, 'JPEG', 0, 0, 1280, 720);
    onProgress?.(i + 1, slides.length);
  }
  pdf.save(`${manifest.id}-slides.pdf`);
}

function slideLabel(slide, index) {
  return slide.title || slide.term || (slide.text ? slide.text.slice(0, 60) : null) || `Slide ${index + 1}`;
}

// Shared text-flow helper for the two text-based (selectable, searchable)
// exports below — deliberately not rasterized, unlike the full deck.
function makeTextPdf(heading, subheading) {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 48;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const maxWidth = pageWidth - marginX * 2;
  let y = 56;

  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18);
  pdf.text(heading, marginX, y); y += 22;
  if (subheading) {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11); pdf.setTextColor(100);
    pdf.text(subheading, marginX, y); y += 22;
    pdf.setTextColor(0);
  }

  function ensureRoom(lineHeight = 15) {
    if (y > pageHeight - 56) { pdf.addPage(); y = 56; }
  }
  function heading2(text) {
    ensureRoom(20);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
    pdf.text(text, marginX, y); y += 18;
  }
  function paragraph(text) {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11);
    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach((line) => { ensureRoom(); pdf.text(line, marginX, y); y += 15; });
    y += 8;
  }
  function spacer(px = 6) { y += px; }

  return { pdf, heading2, paragraph, spacer, get y() { return y; } };
}

export function exportNotesPdf(manifest, slides, notesMap, userName) {
  const doc = makeTextPdf(`${manifest.title} | My Notes`, userName || undefined);
  const withNotes = slides
    .map((slide, i) => ({ slide, i, note: (notesMap[i] || '').trim() }))
    .filter((x) => x.note);

  if (!withNotes.length) {
    doc.paragraph('No notes were taken during this presentation.');
  } else {
    withNotes.forEach(({ slide, i, note }) => {
      doc.heading2(`${i + 1}. ${slideLabel(slide, i)}`);
      doc.paragraph(note);
    });
  }
  doc.pdf.save(`${manifest.id}-notes.pdf`);
}

// Curated quick-reference — pulled from the deck's own setup steps, the five
// prompt habits, the reusable scaffold, and the artifact/skill patterns —
// rather than the whole deck, so it's genuinely a one-page-ish take-home
// rather than a re-export of everything.
export function exportQuickReferenceGuidePdf(manifest, slides) {
  const byId = Object.fromEntries(slides.map((s) => [s.id, s]));
  const doc = makeTextPdf(`${manifest.title} | Setup & Quick Reference`, 'Take-home guide');

  const setup = byId[29]; // Exercise 2.1 | account setup steps
  if (setup) {
    doc.heading2('Getting set up');
    setup.steps.forEach((step, i) => doc.paragraph(`${i + 1}. ${step.title}${step.detail ? ` | ${step.detail}` : ''}`));
    doc.spacer(6);
  }

  const habits = byId[18]; // The five habits of a strong prompt
  if (habits) {
    doc.heading2(habits.title);
    habits.items.forEach((h, i) => doc.paragraph(`${i + 1}. ${h.title} | ${h.description}`));
    doc.spacer(6);
  }

  const scaffold = byId[21]?.scaffold; // Fill-in-the-blanks scaffold
  if (scaffold) {
    doc.heading2(scaffold.label);
    doc.paragraph(scaffold.text);
    doc.spacer(6);
  }

  const artifactPattern = byId[40]?.pattern;
  if (artifactPattern) {
    doc.heading2(artifactPattern.label);
    doc.paragraph(artifactPattern.text);
    doc.spacer(6);
  }

  const skillPattern = byId[48];
  if (skillPattern) {
    doc.heading2(skillPattern.title);
    skillPattern.fields.forEach((f) => doc.paragraph(`${f.label}: ${f.value}`));
  }

  doc.pdf.save(`${manifest.id}-quick-reference.pdf`);
}
